import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Table,
  Header,
  Pagination,
  TextFilter,
  StatusIndicator,
  Box,
  CollectionPreferences,
  Popover,
  Button,
  SpaceBetween,
  type TableProps,
  type CollectionPreferencesProps,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import awsCronParser from 'aws-cron-parser';
import { type TaskDetail, type TaskStatus } from '../../client/ScheduleTaskClient';
import { useTaskData, refreshTasks } from './useTaskData';
import { describeSchedule, formatDateTime } from '../../i18n/format';
import type { Locale } from '../../i18n/resources';
import { useLocale } from '../../i18n/useLocale';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useAgent } from '../../context/AgentContext';

function statusIndicator(status: TaskStatus, t: TFunction<'tasks'>) {
  switch (status) {
    case 'SCHEDULED':
      return <StatusIndicator type="success">{t('status.scheduled')}</StatusIndicator>;
    case 'COMPLETED':
      return <StatusIndicator type="stopped">{t('status.completed')}</StatusIndicator>;
    case 'INVOKE_FAILED':
      return <StatusIndicator type="error">{t('status.invokeFailed')}</StatusIndicator>;
    default:
      return <StatusIndicator type="info">{status}</StatusIndicator>;
  }
}

function getNextRunDate(item: TaskDetail): Date | null {
  try {
    const expr = item.scheduleExpression;
    if (!expr) return null;

    // One-time schedule: at(2026-04-24T14:33:39) — times are UTC
    const atMatch = expr.match(/^at\((.+)\)$/);
    if (atMatch) {
      return item.status === 'SCHEDULED' ? new Date(atMatch[1] + 'Z') : null;
    }

    // AWS cron: cron(0 12 * * ? *)
    const cronMatch = expr.match(/^cron\((.+)\)$/);
    if (cronMatch) {
      const cron = awsCronParser.parse(cronMatch[1]);
      return awsCronParser.next(cron, new Date()) ?? null;
    }

    return null;
  } catch {
    return null;
  }
}

/** Column definitions in the UI locale. Built per render so headers follow a locale change. */
function columnDefinitions(t: TFunction<'tasks'>, locale: Locale): TableProps.ColumnDefinition<TaskDetail>[] {
  return [
  {
    id: 'name',
    header: t('columns.name'),
    cell: (item: TaskDetail) => {
      let displayPayload = item.payload || '-';
      try {
        const parsed = JSON.parse(item.payload);
        if (parsed.prompt) {
          displayPayload = parsed.prompt;
        }
      } catch { /* not JSON, use raw value */ }

      return (
        <Popover
          header={t('columns.payload')}
          content={<div style={{ whiteSpace: 'pre-wrap' }}><Box variant="code">{displayPayload}</Box></div>}
          triggerType="text"
          size="large"
        >
          {item.name || '-'}
        </Popover>
      );
    },
    sortingField: 'name',
  },
  { id: 'schedule', header: t('columns.schedule'), cell: (item: TaskDetail) => describeSchedule(item.scheduleExpression, locale) },
  {
    id: 'nextRun',
    header: t('columns.nextRun'),
    cell: (item: TaskDetail) => {
      const d = getNextRunDate(item);
      return d ? formatDateTime(d, locale) : '-';
    },
    sortingComparator: (a: TaskDetail, b: TaskDetail) =>
      (getNextRunDate(a)?.getTime() ?? Infinity) - (getNextRunDate(b)?.getTime() ?? Infinity),
  },
  { id: 'lastRun', header: t('columns.lastRun'), cell: (item: TaskDetail) => formatDateTime(item.lastExecutedAt, locale), sortingField: 'lastExecutedAt' },
  { id: 'status', header: t('columns.status'), cell: (item: TaskDetail) => statusIndicator(item.status, t), sortingField: 'status' },
  {
    id: 'taskId',
    header: t('columns.taskId'),
    cell: (item: TaskDetail) => (
      <Popover
        header={t('columns.taskId')}
        content={<Box variant="code">{item.taskId}</Box>}
        triggerType="text"
        size="medium"
        dismissButton={false}
      >
        <Box variant="code">{item.taskId}</Box>
      </Popover>
    ),
    sortingField: 'taskId',
  },
  ];
}

/** Column ids, in table order (preferences store ids only). */
const COLUMN_IDS = ['name', 'schedule', 'nextRun', 'lastRun', 'status', 'taskId'];

function visibleContentOptions(t: TFunction<'tasks'>, columns: TableProps.ColumnDefinition<TaskDetail>[]): CollectionPreferencesProps.VisibleContentOptionsGroup[] {
  return [
    {
      label: t('preferences.taskProperties'),
      options: columns.map((col) => ({
        id: col.id!,
        label: typeof col.header === 'string' ? col.header : col.id!,
      })),
    },
  ];
}

function pageSizeOptions(t: TFunction<'tasks'>): CollectionPreferencesProps.PageSizeOption[] {
  return [10, 20, 50].map((n) => ({ value: n, label: t('preferences.pageSizeOption', { n }) }));
}

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 10,
  visibleContent: COLUMN_IDS,
  wrapLines: false,
  stripedRows: false,
};

const PREFS_STORAGE_KEY = 'rocky_task_table_prefs';

const NLP_EXAMPLES = ['scheduled.example1', 'scheduled.example2', 'scheduled.example3', 'scheduled.example4'] as const;

function loadPreferences(): CollectionPreferencesProps.Preferences {
  try {
    const stored = localStorage.getItem(PREFS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return DEFAULT_PREFERENCES;
}

function savePreferences(prefs: CollectionPreferencesProps.Preferences) {
  try {
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch { /* ignore */ }
}

export default function ScheduledTasksTab() {
  const { t } = useTranslation('tasks');
  const { locale } = useLocale();
  const columns = useMemo(() => columnDefinitions(t, locale), [t, locale]);
  const { tasks, tasksLoading: loading, tasksError: error } = useTaskData();
  const { effectiveUid } = useImpersonation();
  const { currentAgent } = useAgent();
  const [preferences, setPreferences] =
    useState<CollectionPreferencesProps.Preferences>(loadPreferences);
  const [alertVisible, setAlertVisible] = useState(true);

  const handleDismissAlert = () => {
    setAlertVisible(false);
  };

  const handlePreferencesChange = (detail: CollectionPreferencesProps.Preferences) => {
    setPreferences(detail);
    savePreferences(detail);
  };

  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(tasks, {
      filtering: {
        empty: (
          <Box textAlign="center" color="inherit">
            <b>{t('scheduled.filterEmptyTitle')}</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              {t('scheduled.filterEmptyBody')}
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>{t('scheduled.noMatchTitle')}</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              {t('scheduled.noMatchBody')}
            </Box>
          </Box>
        ),
      },
      pagination: { pageSize: preferences.pageSize ?? 10 },
      sorting: { defaultState: { sortingColumn: columns[0] } },
      selection: {},
    });

  const visibleColumns = columns.filter(
    (col) => !preferences.visibleContent || preferences.visibleContent.includes(col.id!),
  );

  return (
    <SpaceBetween size="m">
      {alertVisible && (
        <Alert
          dismissible
          onDismiss={handleDismissAlert}
          statusIconAriaLabel={t('scheduled.hintIcon')}
        >
          {t('scheduled.hint')}
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {NLP_EXAMPLES.map((ex) => (
              <li key={ex}>{t(ex)}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Table
        {...collectionProps}
        columnDefinitions={visibleColumns}
        items={items}
        loading={loading}
        loadingText={t('scheduled.loading')}
        selectionType={undefined}
        trackBy="taskId"
        variant="container"
        stickyHeader
        wrapLines={preferences.wrapLines}
        stripedRows={preferences.stripedRows}
        header={
          <Header
            counter={`(${tasks.length})`}
            description={t('scheduled.description')}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="refresh"
                  loading={loading}
                  onClick={() => effectiveUid && refreshTasks(effectiveUid, currentAgent)}
                />
              </SpaceBetween>
            }
          >
            {t('scheduled.title')}
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder={t('scheduled.filterPlaceholder')}
            countText={t('scheduled.matches', { n: filteredItemsCount ?? 0 })}
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title={t('preferences.title')}
            confirmLabel={t('preferences.confirm')}
            cancelLabel={t('preferences.cancel')}
            preferences={preferences}
            onConfirm={({ detail }: { detail: CollectionPreferencesProps.Preferences }) => handlePreferencesChange(detail)}
            pageSizePreference={{ title: t('preferences.pageSize'), options: pageSizeOptions(t) }}
            visibleContentPreference={{ title: t('preferences.visibleColumns'), options: visibleContentOptions(t, columns) }}
            wrapLinesPreference={{ label: t('preferences.wrapLines'), description: t('preferences.wrapLinesDescription') }}
            stripedRowsPreference={{ label: t('preferences.stripedRows'), description: t('preferences.stripedRowsDescription') }}
          />
        }
        empty={
          error ? (
            <Box textAlign="center" color="inherit">
              <b>{t('scheduled.errorTitle')}</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">{error}</Box>
            </Box>
          ) : (
            <Box textAlign="center" color="inherit">
              <b>{t('scheduled.emptyTitle')}</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                {t('scheduled.emptyBody')}
              </Box>
            </Box>
          )
        }
      />
    </SpaceBetween>
  );
}
